import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import * as moment from 'moment';
import { HttpService } from '../service/http.service';
import { QuotationTemplate } from '../quotation-template';
import { BookingPreConfirmationService } from '../service/booking-pre-confirmation.service';
import { BookingViewModeService } from '../service/booking-view-mode.service';
import { BookingService } from '../service/booking.service';
import Decimal from 'decimal.js';
import { ToastMessageService } from '../toast-message.service';
import { BookingPreConfirmation } from '../booking-pre-confirmation';
import { BookingTransactionsService } from '../service/booking-transactions.service';
import { QuotationTemplateService } from '../quotation-template.service';
import { SalesProduct } from '../sales-product';
import { CurrencyService } from '../service/currency.service';
import { WpContactRequestQuotation } from '../wp-contact-request-quotation';
import { BookingQuotation } from '../booking-quotation';
import { SalesProductType } from '../sales-product-type';
import { ProductTypeService } from '../service/product-type.service';
import { Moment } from 'moment';
import { ApiError } from '../api-error';
import { BookingPreConfirmationProduct } from '../booking-pre-confirmation-product';
import * as Editor from '../../libs/ckeditor';
import { QuotationTemplateWithDetail } from '../quotation-template-with-detail';

@Component({
  selector: 'app-booking-pre-confirmation',
  templateUrl: './booking-pre-confirmation.component.html',
  styleUrls: ['./booking-pre-confirmation.component.css']
})
export class BookingPreConfirmationComponent implements OnInit {

  quotationTemplates: QuotationTemplate[] = [];
  quotationTemplateTooltips: string[] = [];
  displayQuotationTemplateDialog = false;
  selectedQuotationTemplate: QuotationTemplate | null = null;
  selectedQuotationTemplateName: string | null = null;
  displayQuotationTemplateCreatingDialog = false;

  public Editor = Editor;

  readonly TRAVEL_INSURANCE_ID = 15;
  readonly GARANTIE_ANNULATION_ID = 7;
  readonly GARANTIE_ANNULATION_RATE = new Decimal(0.04);
  readonly DEFAULT_DEPOSIT_AMOUNT = 250;
  readonly LVI_INSURANCE_ID = 4;

  constructor(
    public bookingViewModeService: BookingViewModeService,
    public bookingService: BookingService,
    public bookingPreConfirmationService: BookingPreConfirmationService,
    private httpService: HttpService,
    private builder: FormBuilder,
    private toastService: ToastMessageService,
    private bookingTransactionsService: BookingTransactionsService,
    public quotationTemplateService: QuotationTemplateService,
    public currencyService: CurrencyService,
    private productTypeService: ProductTypeService
  ) { }

  ngOnInit(): void {
  }

  getQuotationTemplates() {
    if (this.bookingService.booking.school && this.bookingService.booking.student.language) {
      this.httpService.getTemplatesBySchoolAndLanguage(this.bookingService.booking.school.id!, this.bookingService.booking.student.language.id).subscribe(res => {
        this.quotationTemplates = res;
        this.displayQuotationTemplateDialog = true;
        this.updateTooltips();
      });
    }
  }

  updateTooltips() {
    this.quotationTemplateTooltips = [];
    for (let template of this.quotationTemplates) {
      let tooltipMessage = "";
      for (let product of template.products) {
        tooltipMessage = tooltipMessage + product.productType.name + " " + product.amount + " " + product.currency.symbol + "\n";
      }
      this.quotationTemplateTooltips.push(tooltipMessage);
    }
  }

  private get preConfirmationDate(): Moment {
    if (!this.bookingService.booking.preConfirmation!.confirmationDate) {
      return moment().utc(true);
    } else {
      return moment(this.bookingService.booking.preConfirmation!.confirmationDate).utc(true);
    }
  }

  mouseover(i: number) {
    let array = this.bookingPreConfirmationService.preConfirmationForm.get("products") as FormArray;
    array.controls[i].get("activeMenu")!.setValue(true);
  }
  mouseout(i: number) {
    let array = this.bookingPreConfirmationService.preConfirmationForm.get("products") as FormArray;
    array.controls[i].get("activeMenu")!.setValue(false);
  }

  pressTabOnCurrencyDropdown(i: number) {
    let itemArray = this.bookingPreConfirmationService.preConfirmationForm.get("products") as FormArray;
    if (i == itemArray.length - 1) {
      this.bookingPreConfirmationService.insertItem(i);
    }
  }

  onProductCurrencyUpdate() {
    this.bookingPreConfirmationService.updateTotalPriceInCurrency(this.preConfirmationDate);
  }

  baseCurrencyUpdate() {
    this.bookingPreConfirmationService.updateTotalPriceInCurrency(this.preConfirmationDate);
  }

  secondCurrencyUpdate() {
    this.bookingPreConfirmationService.updateTotalPriceInCurrency(this.preConfirmationDate);
  }

  productTypeUpdate(productType: SalesProductType, index: number) {
    if (this.bookingService.booking.student.language) {
      this.productTypeService.setDefaultProductTypeDescription(productType, index, this.bookingPreConfirmationService.preConfirmationForm, this.bookingService.booking.student.language.id);
    }
    this.bookingPreConfirmationService.updateTotalPriceInCurrency(this.preConfirmationDate);
  }

  onConfirmationAmountBlur() {
    this.bookingPreConfirmationService.updateTotalPriceInCurrency(this.preConfirmationDate);
  }

  get productFormArray() {
    let formArray = this.bookingPreConfirmationService.preConfirmationForm.get('products') as FormArray;
    return formArray.controls;
  }

  getProductForms(index: number) {
    return this.productFormArray[index];
  }

  setPreConfirmationFormValuesFromTemplate(selectedItem: QuotationTemplate) {
    this.selectedQuotationTemplate = selectedItem;

    this.bookingPreConfirmationService.preConfirmationForm.get("description")!.setValue(this.selectedQuotationTemplate!.description);
    this.bookingPreConfirmationService.preConfirmationForm.get("comment")!.setValue(this.selectedQuotationTemplate!.comment);
    this.bookingPreConfirmationService.preConfirmationForm.get("products")!.reset();
    let products = this.bookingPreConfirmationService.preConfirmationForm.get("products") as FormArray;
    products.clear();
    this.bookingPreConfirmationService.preConfirmationForm.get("baseCurrency")!.setValue(this.selectedQuotationTemplate!.baseCurrency);
    this.bookingPreConfirmationService.preConfirmationForm.get("secondCurrency")!.setValue(this.selectedQuotationTemplate!.secondCurrency);
    this.bookingPreConfirmationService.preConfirmationForm.get("confirmationDate")!.setValue(moment().toDate());
    this.bookingPreConfirmationService.preConfirmationForm.get("dueDate")!.setValue(null);
    this.bookingPreConfirmationService.preConfirmationForm.get("priceIncludes")!.setValue(this.selectedQuotationTemplate!.priceIncludes);
    this.bookingPreConfirmationService.preConfirmationForm.get("priceNotIncludes")!.setValue(this.selectedQuotationTemplate!.priceNotIncludes);
    let itemArray = this.bookingPreConfirmationService.preConfirmationForm.get("products") as FormArray;
    for (let product of this.selectedQuotationTemplate!.products) {
      let newFormgroup = this.builder.group({
        type: product.productType,
        description: product.description,
        amount: [new Decimal(product.amount), Validators.pattern(/^-?[.\d]+$/)],
        currency: product.currency,
        activeMenu: false,
        id: product.id
      });
      itemArray.push(newFormgroup);
    }
    this.displayQuotationTemplateDialog = false;
    this.quotationTemplateTooltips = [];
    this.updateConfirmation();
    this.bookingPreConfirmationService.updateTotalPriceInCurrency(this.preConfirmationDate);
  }

  updateConfirmation() {
    if (this.bookingPreConfirmationService.preConfirmationForm.invalid) {
      return;
    } else {
      this.validateProductList();
      if (!this.bookingPreConfirmationService.preConfirmationForm.value.selectedBank) {
        this.toastService.addError("Error", "Please select recipient bank account");
        return;
      }
    }
    if (!this.bookingService.booking.preConfirmation) {
      let preConfirmation = new BookingPreConfirmation(
        this.bookingPreConfirmationService.preConfirmationForm.value.description,
        this.bookingPreConfirmationService.preConfirmationForm.value.comment,
        this.setProductList(),
        this.bookingPreConfirmationService.preConfirmationForm.value.baseCurrency,
        this.bookingPreConfirmationService.preConfirmationForm.value.secondCurrency,
        this.bookingPreConfirmationService.preConfirmationForm.value.confirmationDate == null ? null : moment(this.bookingPreConfirmationService.preConfirmationForm.value.confirmationDate).utc(true),
        this.bookingPreConfirmationService.preConfirmationForm.value.dueDate == null ? null : moment(this.bookingPreConfirmationService.preConfirmationForm.value.dueDate).utc(true),
        this.bookingPreConfirmationService.preConfirmationForm.value.deposit,
        this.bookingPreConfirmationService.preConfirmationForm.value.priceIncludes,
        this.bookingPreConfirmationService.preConfirmationForm.value.priceNotIncludes,
        null,
        this.bookingPreConfirmationService.preConfirmationForm.value.selectedBank,
        this.bookingPreConfirmationService.preConfirmationForm.value.footerComment,
        this.hasDepositInReal()
      );
      this.httpService.postBookingPreConfirmation(preConfirmation, this.bookingService.booking.id!).subscribe(preConfirmationId => {
        this.toastService.addSuccess("success", "confirmation added");
        preConfirmation.id = preConfirmationId;
        this.bookingService.booking.preConfirmation = preConfirmation;
        this.bookingTransactionsService.updateBookingFinancialState(this.bookingService.booking.id!, this.bookingService.booking.preConfirmation.baseCurrency.id);
        this.bookingService.sendingUpdateRequest = false;
      }, error => {
        let apiError = new ApiError(error);
        this.toastService.addError("Error", apiError.message);
        this.bookingService.sendingUpdateRequest = false;
      });
    } else {
      this.bookingService.booking.preConfirmation.description = this.bookingPreConfirmationService.preConfirmationForm.value.description;
      this.bookingService.booking.preConfirmation.comment = this.bookingPreConfirmationService.preConfirmationForm.value.comment;
      this.bookingService.booking.preConfirmation.productList = this.setProductList();
      this.bookingService.booking.preConfirmation.baseCurrency = this.bookingPreConfirmationService.preConfirmationForm.value.baseCurrency;
      this.bookingService.booking.preConfirmation.secondCurrency = this.bookingPreConfirmationService.preConfirmationForm.value.secondCurrency;
      this.bookingService.booking.preConfirmation.confirmationDate = this.bookingPreConfirmationService.preConfirmationForm.value.confirmationDate == null ? null : moment(this.bookingPreConfirmationService.preConfirmationForm.value.confirmationDate).utc(true);
      this.bookingService.booking.preConfirmation.dueDate = this.bookingPreConfirmationService.preConfirmationForm.value.dueDate == null ? null : moment(this.bookingPreConfirmationService.preConfirmationForm.value.dueDate).utc(true);
      this.bookingService.booking.preConfirmation.deposit = this.bookingPreConfirmationService.preConfirmationForm.value.deposit;
      this.bookingService.booking.preConfirmation.priceIncludes = this.bookingPreConfirmationService.preConfirmationForm.value.priceIncludes;
      this.bookingService.booking.preConfirmation.priceNotIncludes = this.bookingPreConfirmationService.preConfirmationForm.value.priceNotIncludes;
      this.bookingService.booking.preConfirmation.selectedBank = this.bookingPreConfirmationService.preConfirmationForm.value.selectedBank;
      this.bookingService.booking.preConfirmation.middleComment = this.bookingPreConfirmationService.preConfirmationForm.value.middleComment;
      this.httpService.putBookingConfirmation(this.bookingService.booking.preConfirmation).subscribe(() => {
        this.toastService.addSuccess("success", "confirmation updated");
        this.bookingTransactionsService.updateBookingFinancialState(this.bookingService.booking.id!, this.bookingService.booking.preConfirmation!.baseCurrency.id);
        this.bookingService.sendingUpdateRequest = false;
      }, error => {
        this.bookingService.sendingUpdateRequest = false;
        let apiError = new ApiError(error);
        this.toastService.addError("Error", apiError.message);
      });
    }
  }

  hasDepositInReal() {
    if (this.bookingService.booking.salesAgent.id == 5) {
      return true;
    }
    return false;
  }

  validateProductList() {
    let cancellationInsuranceRequested = this.bookingService.booking.cancellationInsuranceRequested;
    let cancellationInsuranceSupplier = this.bookingService.booking.cancellationInsuranceSupplier;
    let containsCancellationInsurance = false;
    let travelInsuranceRequested = this.bookingService.booking.travelInsuranceRequested;
    let travelInsuranceSupplier = this.bookingService.booking.travelInsuranceSupplier;
    let containsTravelInsurance = false;
    let productFormArray = this.bookingPreConfirmationService.preConfirmationForm.get("products") as FormArray;
    for (let product of productFormArray.controls) {
      if (product.value.type.id == this.GARANTIE_ANNULATION_ID) {
        containsCancellationInsurance = true;
      }
      if (product.value.type.id == this.TRAVEL_INSURANCE_ID) {
        containsTravelInsurance = true;
      }
    }
    if (cancellationInsuranceRequested && !containsCancellationInsurance && (cancellationInsuranceSupplier?.id != this.LVI_INSURANCE_ID)) {
      this.toastService.addError("Error", "This booking needs cancellation insurance !");
      throw new Error("booking and confirmation validation error");
    }
    if (travelInsuranceRequested && !containsTravelInsurance && (travelInsuranceSupplier?.id != this.LVI_INSURANCE_ID)) {
      this.toastService.addError("Error", "This booking needs travel insurance !");
      throw new Error("booking and confirmation validation error");
    }
  }

  setProductList() {
    let products = [];
    let productFormArray = this.bookingPreConfirmationService.preConfirmationForm.get("products") as FormArray;
    for (let product of productFormArray.controls) {
      let confirmationProduct = new BookingPreConfirmationProduct(
        product.get("type")!.value,
        product.get("description")!.value,
        new Decimal(product.get("amount")!.value),
        product.get("currency")!.value,
        null
      );
      products.push(confirmationProduct);
    }
    return products;
  }

  saveQuotationAsTemplate() {
    if (!(this.bookingService.booking.school && this.bookingService.booking.student.language && this.selectedQuotationTemplateName)) {
      this.toastService.addError("Error", "School, Template name or Language of student is missing");
      return;
    }
    this.httpService.checkIfTemplateNameExists(this.selectedQuotationTemplateName, this.bookingService.booking.school.id!, this.bookingService.booking.student.language.id).subscribe(res => {
      if (res == true) {
        this.toastService.addError("Error", "This template name is already used. Please select another name.");
        return;
      } else {
        this.quotationTemplateService.saveBookingPreConfirmationFormAsTemplate(this.bookingPreConfirmationService.preConfirmationForm, this.bookingService.booking.school, this.bookingService.booking.student.language,
          this.selectedQuotationTemplateName!).subscribe(() => {
            this.toastService.addSuccess("Success", "Template saved");
            this.displayQuotationTemplateCreatingDialog = false;
          }, error => this.toastService.addError("Error", error.message));
      }
    }, error => this.toastService.addError("Error", error.message));
  }

  activateQuotationTemplateSavingDialog() {
    this.displayQuotationTemplateCreatingDialog = true;
  }

  activatePreConfirmationForm() {
    if (this.bookingViewModeService.selectedView != this.bookingViewModeService.PRE_CONFIRMATION_VIEW) {
      this.bookingViewModeService.activeItemId = this.bookingViewModeService.PRE_CONFIRMATION_VIEW;
      this.bookingViewModeService.activeItem = this.bookingViewModeService.findMenuItemIndex(this.bookingViewModeService.PRE_CONFIRMATION_VIEW)!;
      this.bookingViewModeService.selectedView = this.bookingViewModeService.PRE_CONFIRMATION_VIEW;
    }
    if (this.bookingPreConfirmationService.preConfirmationForm.value.selectedBank) {
      this.bookingPreConfirmationService.displayPreConfirmationForm = true;
    } else {
      this.toastService.addError("Error", "Please select the recipient bank.");
    }
  }

  handlePreConfirmationCreatedEvent(finished: boolean) {
    if (finished) {
      this.bookingPreConfirmationService.displayPreConfirmationForm = false;
    }
  }

  activateQuotationTemplateEditDialog(template: QuotationTemplate) {
    this.quotationTemplateService.activateEditDialog = true;
    this.selectedQuotationTemplate = template;
  }

  updateModifiedQuotationTemplate(updatedTemplate: QuotationTemplateWithDetail) {
    for (let template of this.quotationTemplates) {
      if (template.id === updatedTemplate.template.id) {
        template.products = updatedTemplate.template.products;
        template.name = updatedTemplate.template.name;
        template.lastModified = updatedTemplate.template.lastModified;
      }
    }
    this.updateTooltips();
  }
}
